import { Table } from "@/components/shared/ui/RegistryTable";
import { useGetCreditRetirementListForRegistryPagination } from "@/hooks/registry/useGetCreditRetirementListForRegistryPagination";
import { feRoutes } from "@/routes/feRoutes";
import type { CreditRetirementListItem } from "@/types/creditTracking";
import { useMemo } from "react";
import { FullPageLoader } from "../shared/ui/Loader";
import PaginationTwo from "../shared/ui/PaginationTwo";

const formatRetirementId = (id: number) => String(id).padStart(5, "0");
const formatRetirementDate = (isoDate: string) => isoDate.slice(0, 10);

const formatRetirementPurpose = (metadata: CreditRetirementListItem["metadata"]) => {
	if (!metadata?.purpose?.length) return " — ";
	return metadata.purpose
		.filter((p) => p != "Other")
		.map((p) => {
			if (p == "Compliance / Regulatory") {
				return "Compliance/Regulatory";
			}
			return p;
		})
		.join(", ");
};

interface Props {
	hidePagination?: boolean;
}

const CreditRetirementTableWrapper = ({ hidePagination = false }: Props) => {
	const { creditRetirementList, pagination } = useGetCreditRetirementListForRegistryPagination();
	const { data: retirementListData, isLoading } = creditRetirementList;
	const { changeOffset } = pagination;

	const list = retirementListData?.data ?? [];

	const handleRowClick = (row: CreditRetirementListItem) => {
		window.open(feRoutes.registry.retirementDetails(row.creditRetirementId), "_blank");
	};

	const columns = useMemo(
		() => [
			{
				header: "ID",
				cell: (row: { row: { original: CreditRetirementListItem } }) => (
					<div className="min-w-[60px]">{formatRetirementId(row.row.original.creditRetirementId)}</div>
				),
				accessorKey: "creditRetirementId",
			},
			{
				header: "Retirement Date",
				cell: (row: { row: { original: CreditRetirementListItem } }) => (
					<div className="min-w-[110px]">{formatRetirementDate(row.row.original.eventDate)}</div>
				),
				accessorKey: "eventDate",
			},
			{
				// According to BE documentation, beneficiary property stands for Credit owner
				header: "Retired By",
				cell: (row: { row: { original: CreditRetirementListItem } }) => (
					<div className="min-w-[200px] max-w-[350px] whitespace-normal break-words">
						{row.row.original.beneficiary.legalName}
					</div>
				),
				accessorKey: "beneficiary",
			},
			{
				header: "Project",
				cell: (row: { row: { original: CreditRetirementListItem } }) => (
					<div className="min-w-[180px] whitespace-normal break-words">{row.row.original.projectTitle}</div>
				),
				accessorKey: "projectTitle",
			},
			{
				header: "Purpose",
				cell: (row: { row: { original: CreditRetirementListItem } }) => {
					const metadata = row.row.original.metadata;
					const labels = formatRetirementPurpose(metadata);
					return (
						<div
							className={`flex ${
								labels == " — " ? "w-[20px]" : "w-[240px]"
							} flex-col gap-y-0.5 whitespace-normal break-words`}
						>
							<span>
								{labels}
								{metadata?.otherText && <span>, {metadata.otherText}</span>}
							</span>
						</div>
					);
				},
				accessorKey: "metadata",
			},
			{
				header: "Beneficiary",
				cell: (row: { row: { original: CreditRetirementListItem } }) => (
					<div className="min-w-[200px] max-w-[350px] whitespace-normal break-words">
						{row.row.original.retireOnBehalfOf ?? row.row.original.beneficiary.legalName}
					</div>
				),
				accessorKey: "retireOnBehalfOf",
			},
			{
				header: () => <div className="w-full text-right">Credits</div>,
				cell: (row: { row: { original: CreditRetirementListItem } }) => (
					<div className="min-w-[80px] text-right">{row.row.original.totalCreditCount.toLocaleString("en-US")}</div>
				),
				accessorKey: "totalCreditCount",
			},
		],
		[],
	);

	if (isLoading) return <FullPageLoader />;

	return (
		<div>
			{(!list || list.length === 0) && (
				<div className="flex justify-center py-20">
					<p>No retirements yet.</p>
				</div>
			)}

			{list && list.length > 0 && (
				<div>
					{!hidePagination && retirementListData && retirementListData.count > list.length && (
						<div className="mt-8 flex w-full justify-end py-0 pb-0 pr-0 md:px-8">
							<PaginationTwo
								count={retirementListData.count}
								changeOffset={changeOffset}
								offset={parseInt(retirementListData.offset, 10)}
								limit={parseInt(retirementListData.limit, 10)}
							/>
						</div>
					)}
					<Table data={list} columns={columns} onRowClick={handleRowClick} />
				</div>
			)}
		</div>
	);
};

export default CreditRetirementTableWrapper;
